/**
 * Dashboard Page Component
 * 
 * Main dashboard page for authenticated users.
 * Displays user information, competitions, and quick actions.
 */

import React, { useMemo, useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { authUtils } from '../services/auth';
import Button from '../components/common/Button';
import Header from '../components/common/Header';
import ListControls from '../components/common/ListControls';
import PageTitle from '../components/common/PageTitle';
import YourCompetitions from './dashboard/YourCompetitions';
import { useAccountCounts } from '../components/account/useAccountCounts';
import { Competition } from '../types/interfaces';
import { SkeletonGroup } from '../components/common/Skeleton';
import CompetitionCard, { CompetitionCardSkeleton } from './dashboard/CompetitionCard';
import './Dashboard.css';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

/** With every status shown, live competitions come first. Unknown statuses go last. */
const STATUS_ORDER = ['open', 'new', 'closed', 'archived'];
const statusRank = (status?: string) => {
  const i = STATUS_ORDER.indexOf((status || '').toLowerCase());
  return i < 0 ? STATUS_ORDER.length : i;
};



/**
 * Dashboard page component
 * @returns {JSX.Element} Dashboard page component
 */
const Dashboard: React.FC = () => {
  useDocumentTitle('Dashboard');
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const { data, loading: countsLoading } = useAccountCounts();

  // Load competitions data
  useEffect(() => {
    const loadCompetitions = async () => {
      try {
        setLoading(true);
        const token = authUtils.getStoredToken();
        const response = await apiService.post('/competitions/list', {}, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setCompetitions(response.data);
      } catch (err) {
        setError('Failed to load competitions');
        console.error('Error loading competitions:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCompetitions();
  }, []);

  const typeOptions = useMemo(() => {
    const normalizedTypes = competitions
      .map((c) => (c.type || 'unknown').toLowerCase())
      .filter((value, index, all) => all.indexOf(value) === index)
      .sort((a, b) => a.localeCompare(b));

    return [
      { label: 'All types', value: 'all' },
      ...normalizedTypes.map((type) => ({
        label: type === 'unknown' ? 'Unknown' : type.toUpperCase(),
        value: type,
      })),
    ];
  }, [competitions]);

  const filteredCompetitions = useMemo(() => {
    const q = search.trim().toLowerCase();
    return competitions.filter((c) => {
      const matchesQuery =
        !q ||
        (c.title || '').toLowerCase().includes(q) ||
        (c.description || '').toLowerCase().includes(q);
      const status = (c.status || '').toLowerCase();
      const matchesStatus = statusFilter === 'all' || status === statusFilter;
      const type = (c.type || 'unknown').toLowerCase();
      const matchesType = typeFilter === 'all' || type === typeFilter;
      return matchesQuery && matchesStatus && matchesType;
    }).sort((a, b) => statusRank(a.status) - statusRank(b.status));
  }, [competitions, search, statusFilter, typeFilter]);

  return (
    <div className="dashboard">
      <Header />

      {/* Main Content */}
      <main className="dashboard__main">
        <div className="container">
          <PageTitle title="Competitions" />
          <YourCompetitions
            data={data}
            loading={countsLoading}
            onBrowse={() => document.getElementById('all-competitions')?.scrollIntoView({ behavior: 'smooth' })}
          />

          {/* Competitions Section */}
          <section className="dashboard__competitions">
            <div className="dashboard__section-header">
              <h2 id="all-competitions" className="dashboard__list-title">All competitions</h2>
            </div>

            {!loading && !error && (
              <ListControls
                searchValue={search}
                onSearchChange={setSearch}
                searchPlaceholder="Search competitions..."
                sticky
                statusValue={statusFilter}
                onStatusChange={setStatusFilter}
                statusOptions={[
                  { label: 'All', value: 'all' },
                  { label: 'Open', value: 'open' },
                  { label: 'Closed', value: 'closed' },
                  { label: 'Archived', value: 'archived' },
                  { label: 'New', value: 'new' },
                ]}
                typeValue={typeFilter}
                onTypeChange={setTypeFilter}
                typeOptions={typeOptions}
              />
            )}

            {loading ? (
              <SkeletonGroup label="Loading competitions" className="dashboard__competitions-grid">
                {Array.from({ length: 6 }, (_, i) => <CompetitionCardSkeleton key={i} />)}
              </SkeletonGroup>
            ) : error ? (
              <div className="dashboard__error">
                <p>{error}</p>
                <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
                  Retry
                </Button>
              </div>
            ) : filteredCompetitions.length === 0 ? (
              <p className="dashboard__empty">
                No competitions match these filters.{' '}
                {(search || statusFilter !== 'all' || typeFilter !== 'all') && (
                  <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setStatusFilter('all'); setTypeFilter('all'); }}>Clear filters</Button>
                )}
              </p>
            ) : (
              <div className="dashboard__competitions-grid" data-tour="competition-grid">
                {filteredCompetitions.map((competition) => (
                  <CompetitionCard key={competition.id} competition={competition} />
                ))}
              </div>
            )}
          </section>


        </div>
      </main>
    </div>
  );
};

export default Dashboard;
